import { css, StyleDeclarationValue } from 'aphrodite';
import * as React from 'react';
import Measure, { ContentRect } from 'react-measure';
import { getLogoOverlayGeometry, ISize } from '../../../models/utils/logoOverlay';
import { styleSheet } from './styles';

const EMPTY_SIZE: ISize = { height: 0, width: 0 };

const readIntrinsicSize = (image: HTMLImageElement): ISize => ({
	height: image.naturalHeight,
	width: image.naturalWidth,
});

/**
 * Sizes an image that may already be in the browser cache.
 *
 * A cached image finishes loading before React attaches `onLoad`, so that event alone leaves the
 * overlay unmeasured and invisible — which is the common case here, since the picture is chosen in
 * the same session it is previewed in.
 */
function useIntrinsicSize(url: string | undefined) {
	const ref = React.useRef<HTMLImageElement | null>(null);
	const [size, setSize] = React.useState<ISize>(EMPTY_SIZE);

	React.useEffect(() => {
		const image = ref.current;
		setSize(image?.complete && image.naturalWidth > 0 ? readIntrinsicSize(image) : EMPTY_SIZE);
	}, [url]);

	const onLoad = (event: React.SyntheticEvent<HTMLImageElement>) => setSize(readIntrinsicSize(event.currentTarget));

	return { onLoad, ref, size };
}

interface IPreviewProps {
	hideWhiteBackground?: boolean;
	imageUrl: string;
	/** The overlay's longest edge in the image's own pixels, as the publisher applies it. */
	logoSizePx: number;
	/** Unset draws the image on its own, for previewing the post without the overlay. */
	logoUrl?: string;
	styles?: StyleDeclarationValue[];
}

export const LogoOverlayPreview: React.FC<IPreviewProps> = ({
	hideWhiteBackground,
	imageUrl,
	logoSizePx,
	logoUrl,
	styles = [],
}) => {
	const [renderedSize, setRenderedSize] = React.useState<ISize>(EMPTY_SIZE);
	const image = useIntrinsicSize(imageUrl);
	const logo = useIntrinsicSize(logoUrl);

	const onResize = (contentRect: ContentRect) =>
		setRenderedSize({ height: contentRect.client?.height ?? 0, width: contentRect.client?.width ?? 0 });

	const geometry = getLogoOverlayGeometry({
		imageIntrinsicSize: image.size,
		imageRenderedSize: renderedSize,
		logoIntrinsicSize: logo.size,
		logoSizePx,
	});

	return (
		// innerRef hands the image to image.ref, so the element's own ref is measureRef, which never changes. A
		// new ref each render would re-observe the image, and every observation renders again.
		<Measure client innerRef={image.ref} onResize={onResize}>
			{({ measureRef }) => (
				<div className={css(styleSheet.preview, ...styles)}>
					<img alt='' className={css(styleSheet.previewImage)} onLoad={image.onLoad} ref={measureRef} src={imageUrl} />
					{logoUrl ? (
						<span
							className={css(styleSheet.logo, hideWhiteBackground && styleSheet.logoTransparent)}
							style={geometry ? { padding: geometry.padding } : { visibility: 'hidden' }}
						>
							<img
								alt='Account logo overlay'
								onLoad={logo.onLoad}
								ref={logo.ref}
								src={logoUrl}
								style={geometry?.size}
							/>
						</span>
					) : null}
				</div>
			)}
		</Measure>
	);
};

interface ISliderProps {
	label?: string;
	max: number;
	min: number;
	onChange: (size: number) => void;
	step?: number;
	value: number;
}

// A native range input: components-v2 has no slider.
export const LogoSizeSlider: React.FC<ISliderProps> = ({
	label = 'Logo Size',
	max,
	min,
	onChange,
	step = 10,
	value,
}) => {
	const labelId = React.useId();
	return (
		<div className={css(styleSheet.sliderContainer)}>
			<div className={css(styleSheet.sliderHeader)}>
				<span id={labelId}>{label}</span>
				<span aria-atomic='true' aria-live='polite' className={css(styleSheet.sliderValue)}>
					{value}px
				</span>
			</div>
			<input
				aria-labelledby={labelId}
				aria-valuemax={max}
				aria-valuemin={min}
				aria-valuenow={value}
				aria-valuetext={`${value} pixels`}
				className={css(styleSheet.slider)}
				max={max}
				min={min}
				onChange={event => onChange(parseInt(event.target.value, 10))}
				step={step}
				type='range'
				value={value}
			/>
		</div>
	);
};
